import MaterialTable from 'material-table';
import { defaultGridOptions, parseCLDR26, tooltipDelay, useGridColDefs } from '../../utils/GridUtils';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import { Button, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import { centeredCell } from '../../constants/StyleConstants';
import AnimatedButton from '../../components/UI/AnimatedButton';
import { useContext, useEffect, useState } from 'react';
import { LoginContext } from '../../contexts/LoginContext';
import FormatListNumberedOutlinedIcon from '@material-ui/icons/FormatListNumberedOutlined';
import ListAltIcon from '@material-ui/icons/ListAlt';
import CheckIcon from '@material-ui/icons/Check';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Fragment } from 'react';
import LogisticsConfirmationModal from './LogisticsConfirmationModal';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import BlockRoundedIcon from '@material-ui/icons/BlockRounded';
import { AppContext } from '../../contexts/AppContext';
import ChatIcon from '@material-ui/icons/Chat';
import clsx from 'clsx';
import LogisticsCommentsModal from './LogisticsCommentsModal';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { SORT } from './LogisticsPresenter';

const useStyles = makeStyles(theme => ({
  centeredCell,
  root: {
    marginTop: '3em',
  },
  gridActions: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: '1em',
  },
  gridControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  gridButtonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '1em',
  },
  gridReference: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    columnGap: '1em',
    fontSize: '.8em',
  },
  reference: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftSideButton: {
    borderRadius: '1em',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  rightSideButton: {
    borderRadius: '1em',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  activeGlow: {
    color: 'rgb(0, 255, 0)',
    boxShadow: '0px 0px 10px 0px rgb(0, 255, 0, .5)',
    borderRadius: '50%',
    margin: 0,
    stroke: 'rgb(0, 255, 0, .5)',
    strokeWidth: 4,
    strokeOpacity: 0.7,
  },
  hasNoComments: {
    color: '#BDBDBD',
  },
  sortButton: {
    '&.MuiButton-root': {
      minWidth: 'unset',
    },
    '&.MuiButton-startIcon': {
      margin: 0,
    },
  },
}));

const InvoicedApprovedButtons = ({
  showInvoicedApproved,
  setShowInvoicedApproved,
  showNotInvoicedApproved,
  setShowNotInvoicedApproved,
}) => {
  const classes = useStyles();
  const { isSupplier } = useContext(LoginContext);
  const { showWarning } = useContext(AppContext);

  const preventNotSelection = unselection => {
    if (!unselection) {
      showWarning('Debe tener seleccionado al menos un tipo de estado a visualizar');
      return false;
    }

    return true;
  };

  return (
    <div>
      <Button
        className={classes.leftSideButton}
        variant={showInvoicedApproved ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        startIcon={isSupplier ? <MonetizationOnOutlinedIcon /> : <CheckCircleOutlinedIcon />}
        onClick={() =>
          preventNotSelection(!showInvoicedApproved || showNotInvoicedApproved) && setShowInvoicedApproved(!showInvoicedApproved)
        }>
        {isSupplier ? 'Facturados' : 'Aprobados'}
      </Button>
      <Button
        className={classes.rightSideButton}
        variant={showNotInvoicedApproved ? 'contained' : 'outlined'}
        color="secondary"
        size="small"
        startIcon={<CancelOutlinedIcon />}
        onClick={() =>
          preventNotSelection(showInvoicedApproved || !showNotInvoicedApproved) && setShowNotInvoicedApproved(!showNotInvoicedApproved)
        }>
        {isSupplier ? 'No Facturados' : 'No Aprobados'}
      </Button>
    </div>
  );
};

const SortButtons = ({ sort, setSort }) => {
  const classes = useStyles();

  return (
    <div>
      <span style={{ fontSize: '.8em' }}>Ordenar por Nro. de Remito </span>
      <Button
        className={clsx(classes.leftSideButton, classes.sortButton)}
        variant={sort === SORT.DOWNWARD ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        onClick={() => (sort === SORT.DOWNWARD ? setSort(SORT.NONE) : setSort(SORT.DOWNWARD))}>
        <ArrowDownwardIcon style={{ fontSize: '1em' }} />
      </Button>
      <Button
        stlye={{ padding: 0 }}
        className={clsx(classes.rightSideButton, classes.sortButton)}
        variant={sort === SORT.UPWARD ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        onClick={() => (sort === SORT.UPWARD ? setSort(SORT.NONE) : setSort(SORT.UPWARD))}>
        <ArrowUpwardIcon style={{ fontSize: '1em' }} />
      </Button>
    </div>
  );
};

const GridActions = ({
  selectedRows,
  setOpen,
  handleExport,
  loadingExport,
  handleApproval,
  loadingApproval,
  sort,
  setSort,
  ...restProps
}) => {
  const classes = useStyles();
  const { isAllowedByRole, isSupplier } = useContext(LoginContext);

  const handleCheckIn = () => {
    setOpen(true);
  };

  const [loadingExportSelection, setLoadingExportSelection] = useState(false);
  const [loadingExportAll, setLoadingExportAll] = useState(false);

  useEffect(() => {
    !loadingExport && setLoadingExportSelection(false);
    !loadingExport && setLoadingExportAll(false);
  }, [loadingExport]);

  const exportSelection = () => {
    setLoadingExportSelection(true);
    handleExport(selectedRows.map(r => r.reference));
  };

  const exportAll = () => {
    setLoadingExportAll(true);
    handleExport();
  };
  const countSelectedRows = selectedRows.isEmpty()? "": selectedRows.length;
  return isSupplier ? (
    <div className={classes.gridControls}>
      <div className={classes.gridButtonsWrapper}>
        <AnimatedButton
          disabled={selectedRows.isEmpty()}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<FormatListNumberedOutlinedIcon />}
          onClick={() => isAllowedByRole() && exportSelection()}
          loading={loadingExportSelection}>
          Exportar selección
        </AnimatedButton>
        <AnimatedButton
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ListAltIcon />}
          onClick={() => isAllowedByRole() && exportAll()}
          loading={loadingExportAll}>
          Exportar todo
        </AnimatedButton>
        <Tooltip placement="top" arrow title={selectedRows.isEmpty() ? 'Seleccione al menos un registro a facturar' : ''}>
          <div>
            <Button
              disabled={selectedRows.isEmpty()}
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CheckIcon />}
              onClick={() => isAllowedByRole() && handleCheckIn()}>
              Mandar a Facturar
            </Button>
          </div>
        </Tooltip>
        <InvoicedApprovedButtons {...restProps} />
      </div>
      <SortButtons sort={sort} setSort={setSort} />
    </div>
  ) : (
    <div className={classes.gridControls}>
      <div className={classes.gridButtonsWrapper}>
        <Tooltip placement="top" arrow title={selectedRows.isEmpty() ? 'Seleccione al menos un registro a aprobar' : ''}>
          <div>
            <AnimatedButton
              disabled={loadingApproval || selectedRows.isEmpty()}
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => isAllowedByRole() && handleApproval()}
              loading={loadingApproval}>
              Aprobar {countSelectedRows}
            </AnimatedButton>
          </div>
        </Tooltip>
        <InvoicedApprovedButtons {...restProps} />
      </div>
      <SortButtons sort={sort} setSort={setSort} />
    </div>
  );
};

const LogisticsGrid = ({
  data,
  loading,
  tableRef,
  disableSelectAll,
  reloadGrid,
  updateGrid,
  selectedRows,
  setSelectedRows,
  open,
  setOpen,
  cuit,
  gridActions,
}) => {
  const classes = useStyles();
  const { isSupplier } = useContext(LoginContext);
  const [openComments, setOpenComments] = useState(false);
  const [commentParams, setCommentParams] = useState({});

  const handleComments = rowData => {
    setCommentParams({
      cuit,
      reference: rowData.reference,
      hasComments: rowData.hasAcerBragComments,
    });
    setOpenComments(true);
  };
  const BoldValue = ({ value }) => {
    return (<b>{value}</b>);
  };
  
  const colDefs = useGridColDefs([
    {
      title: '',
      field: 'hasAcerBragComments',
      valueType: 'icon',
      hidden: isSupplier,
      valueRender: (value, rowData) => {
        if (!isSupplier) {
          return (
            <div className={classes.centeredCell}>
              <Tooltip
                title={value ? 'Click para ver/editar comentarios' : 'Click para agregar comentarios'}
                enterDelay={tooltipDelay}
                enterNextDelay={tooltipDelay}>
                <span>
                  <IconButton aria-label="visualizar" size="small" color="primary" onClick={() => handleComments(rowData)}>
                    <ChatIcon className={clsx({ [classes.hasNoComments]: !value })} />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          );
        }
      },
    },
    { title: 'Nro. Remito', field: 'reference', valueType: 'numeric', valueRender: (value, rowData) => <BoldValue value={value} /> },
    { title: 'Hoja de servicio', field: 'serviceSheet' },
    { title: 'Nro. Transporte', field: 'shipperCode', valueType: 'numeric' },
    { title: 'Nombre', field: 'shipperName' },
    { title: 'Acreedor', field: 'supplierId' },
    { title: 'Fecha contable', field: 'accountingDate', valueType: 'date' },
    { title: 'Cliente', field: 'clientId' },
    { title: 'Nombre del cliente', field: 'clientName' },
    {
      title: 'Valor Neto',
      field: 'netAmmount',
      valueType: 'numeric',
      valueRender: (value, rowData) => `ARS ${parseCLDR26(value)}`,
    },
    { title: 'Fecha de aprobación', field: 'dateApproved', valueType: 'date' },
    {
      title: 'Estado',
      field: 'acerBragApproved',
      valueType: 'icon',
      valueRender: (value, rowData) => {
        if (isSupplier) {
          return (
            <div className={classes.centeredCell}>
              {rowData.supplierInvoiced ? (
                <Tooltip title="Facturado" enterDelay={tooltipDelay} enterNextDelay={tooltipDelay}>
                  <MonetizationOnOutlinedIcon color="primary" />
                </Tooltip>
              ) : (
                <Tooltip title="No Facturado" enterDelay={tooltipDelay} enterNextDelay={tooltipDelay}>
                  <CancelOutlinedIcon color="secondary" />
                </Tooltip>
              )}
            </div>
          );
        } else {
          return (
            <div className={classes.centeredCell}>
              {rowData.acerBragApproved ? (
                <Tooltip title="Aprobado" enterDelay={tooltipDelay} enterNextDelay={tooltipDelay}>
                  <CheckCircleOutlinedIcon color="primary" />
                </Tooltip>
              ) : (
                <Tooltip title="No Aprobado" enterDelay={tooltipDelay} enterNextDelay={tooltipDelay}>
                  <CancelOutlinedIcon color="secondary" />
                </Tooltip>
              )}
            </div>
          );
        }
      },
    },
    { /*title: 'Fecha de Aprobación', field: 'accountingDate', valueType: 'date'  */},
  ]);

  return (
    <Fragment>
      <LogisticsConfirmationModal
        open={open}
        setOpen={setOpen}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        reloadGrid={reloadGrid}
      />
      {!isSupplier && (
        <LogisticsCommentsModal open={openComments} setOpen={setOpenComments} commentParams={commentParams} updateGrid={updateGrid} />
      )}
      <div className={classes.root}>
        <div className={classes.gridActions}>
          <GridActions selectedRows={selectedRows} loading={loading} setOpen={setOpen} {...gridActions} />
        </div>
        <MaterialTable
          {...defaultGridOptions}
          options={{
            ...defaultGridOptions.options,
            pageSizeOptions: [...defaultGridOptions.options.pageSizeOptions, defaultGridOptions.options.pageSizeOptions.last() * 2],
            selection: true,
            headerSelectionProps: { color: 'primary', disabled: disableSelectAll },
            selectionProps: rowData => {
              return {
                color: 'primary',
                disabled: isSupplier ? rowData.supplierInvoiced : rowData.acerBragApproved,
                indeterminate: isSupplier ? rowData.supplierInvoiced : rowData.acerBragApproved,
                indeterminateIcon: <BlockRoundedIcon />,
              };
            },
          }}
          onSelectionChange={(rows, row) => setSelectedRows(rows.filter(r => (isSupplier ? !r.supplierInvoiced : !r.acerBragApproved)))}
          columns={colDefs}
          data={data}
          isLoading={loading}
          tableRef={tableRef}
        />
      </div>
    </Fragment>
  );
};

export default LogisticsGrid;
